import { AnalyticsManager } from 'utils/analytics/AnalyticsManager'
import { ActionDataKey, ActionName } from 'utils/analytics/AnalyticsManager/AnalyticsManager.types'
import { CONSTANTS } from 'utils/constants'
import { getItemId, formatPriceAsFloat } from 'utils/utils'
import dependencies from 'utils/analytics/AnalyticsManager/AnalyticsManager.dependencies'

import type {
	Action,
	PurchaseAction,
	ViewItemAction,
	AddToCartAction,
	BeginCheckoutAction,
	ViewCartAction,
	ViewItemListAction,
	SelectPromotionAction,
	ViewPromotionAction,
	RemoveFromCartAction,
	LocalizationStartAction,
	LocalizationSelectOrderTypeAction,
	LocalizationFinishAction,
	CouponAction,
	CouponFilterCouponsAction,
	SetUserPropertiesAction,
	SetSuperPropertiesAction,
} from 'utils/analytics/AnalyticsManager/AnalyticsManager.types'

import {EVENTS as LOCALIZATION_EVENTS} from 'mobx/Localization/constants'

/**
 * We cannot refactor the system to use the new analytics manager as is,
 * This adapter is a temporary solution to allow us to use the new analytics manager.
 * It will apply the needed normalization in order to support It.
 */

type EnhancedEcommerceEvent = keyof typeof CONSTANTS.ANALYTICS.ENHANCED_ECOMMERCE_EVENTS

const enhancedEcommerceEvents = CONSTANTS.ANALYTICS.ENHANCED_ECOMMERCE_EVENTS

const getItemsData = (cartFields: any, restData: any) => {
	const itemIds: string[] = []
	let itemsNumber = 0

	Object.keys(cartFields.items).forEach((_key) => {
		const _itemArray = cartFields.items[_key]
		_itemArray.forEach((_itemElement: any) => {
			const _fullItem = restData.items[_itemElement.itemId]

			itemIds.push(getItemId(_fullItem))
			itemsNumber += _itemElement.quantity
		})
	})

	return {
		itemIds,
		itemsNumber,
	}
}
class AnalyticsAdapter {
	private analyticsManager: AnalyticsManager | null

	constructor() {
		this.analyticsManager = null
	}

	public init(tenantId: string, host: string) {
		this.analyticsManager = new AnalyticsManager(tenantId, host, dependencies)
	}

	public async sendEnhancedEcommerceEvent(eventType: EnhancedEcommerceEvent, ...args: any[]) {
		try {
			return this.sendEvent(eventType, ...args)
		} catch (error) {
			console.error('Error sending enhanced ecommerce event', error)
			return null
		}
	}

	public async sendCustomEvent(data: any) {
		try {
			return this.sendEvent('', data)
		} catch (error) {
			console.error('Error sending custom event', error)
			return null
		}
	}

	public async sendPageViewEvent(path: string) {
		try {
			return this.sendEvent('pageView', path)
		} catch (error) {
			console.error('Error sending page view event', error)
			return null
		}
	}

	public async identifyUser(userId: string) {
		return this.analyticsManager?.identifyUser(userId)
	}

	public async resetUser() {
		return this.analyticsManager?.resetUser()
	}

	public async setSuperProperties(properties: SetSuperPropertiesAction) {
		return this.analyticsManager?.sendEvent(ActionName.SET_SUPER_PROPERTIES, properties)
	}

	public async setUserProperties(properties: SetUserPropertiesAction) {
		return this.analyticsManager?.sendEvent(ActionName.SET_USER_PROPERTIES, properties)
	}

	private async sendEvent(eventType: string, ...args: any[]) {
		if (!this.analyticsManager) {
			return
		}

		let actionData: Action | null = null
		let actionName: ActionName | '' = ''

		// normalize enhanced e-commerce events
		if (eventType && enhancedEcommerceEvents[eventType as EnhancedEcommerceEvent]) {
			switch (eventType) {
				case enhancedEcommerceEvents.sendPurchaseEvent: {
					const [itemArray, orderConfirmation, metaData] = args

					const _items: string[] = []
					let numberOfItems = 0

					itemArray.forEach((_itemElement: any, _index: number) => {
						_items.push(getItemId(_itemElement))

						numberOfItems += _itemElement.quantity || 1 // the courseList from the orderConfirm API doesn't group the same items together but has 1 menu-item per element so the quantity will then always be 1
					})

					actionName = ActionName.PURCHASE
					actionData = {
						[ActionDataKey.ORDER_NUMBER]: orderConfirmation?.id,
						[ActionDataKey.TICTUK_ORDER_ID]: orderConfirmation?.tictuk_orderid,
						[ActionDataKey.SHIPPING_TIER]: orderConfirmation?.shipping_tier,
						[ActionDataKey.AFFILIATION]: orderConfirmation?.affiliation,
						[ActionDataKey.PAYMENT_TYPE]: orderConfirmation?.payment_type,
						[ActionDataKey.REVENUE]: parseFloat(orderConfirmation?.revenue || 0),
						[ActionDataKey.PRODUCT_ITEM_IDS]: _items,
						[ActionDataKey.TAX]: orderConfirmation?.tax,
						[ActionDataKey.DELIVERY_CHARGE]: orderConfirmation?.shipping,
						[ActionDataKey.IS_FIRST_PURCHASE]: orderConfirmation?.isFirstPurchase,
						[ActionDataKey.CURRENCY]: metaData?.currency,
						[ActionDataKey.COUPON]: orderConfirmation?.coupon,
						[ActionDataKey.USER_EMAIL]: metaData?.user?.email,
						[ActionDataKey.USER_FIRST_NAME]: metaData?.user?.first_name,
						[ActionDataKey.USER_LAST_NAME]: metaData?.user?.last_name,
						[ActionDataKey.USER_PHONE_NUMBER]: metaData?.user?.phone_number,
						[ActionDataKey.USER_STATUS]: metaData?.user?.status,
						[ActionDataKey.NUMBER_OF_ITEMS]: numberOfItems,
						[ActionDataKey.STORE_ID]: orderConfirmation?.storeId
					} as PurchaseAction
					break
				}
				/**
				 * we never fire the sendViewProdutDetailEvent,
				 * We use sendProductClickEvent instead.
				 * That is how we do it in all the other places.
				 * It is wrong but we are not changing it as part of this task
				 */
				case enhancedEcommerceEvents.sendProductClickEvent: {
					const [itemData, eventMetaData, restData] = args

					actionName = ActionName.VIEW_ITEM
					actionData = {
						[ActionDataKey.ITEM_NAME]: eventMetaData?.itemTitle,
						[ActionDataKey.ITEM_ID]: getItemId(itemData),
						[ActionDataKey.CURRENCY]: restData?.currency,
						[ActionDataKey.ITEM_SECTION_TITLE]: eventMetaData?.sectionTitle,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(itemData?.price, restData?.currency, restData?.countryCode, 1, false, true),
					} as ViewItemAction
					break
				}
				case enhancedEcommerceEvents.sendAddToCartEvent: {
					const [itemData, eventMetaData, cartFields] = args

					actionName = ActionName.ADD_TO_CART
					actionData = {
						[ActionDataKey.QUANTITY]: itemData?.quantity || 1,
						[ActionDataKey.ITEM_NAME]: eventMetaData?.itemTitle,
						[ActionDataKey.ITEM_ID]: getItemId(itemData),
						[ActionDataKey.CURRENCY]: eventMetaData?.currency,
						[ActionDataKey.ITEM_SECTION_TITLE]: eventMetaData?.sectionTitle,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(itemData?.price, cartFields?.currency, cartFields?.countryCode, 1, false, true),
						[ActionDataKey.COUPONS]: cartFields?.coupons || [],
					} as AddToCartAction
					break
				}
				case enhancedEcommerceEvents.sendCheckOutEvent: {
					const [restData, stepData, cartFields] = args

					if (
						stepData?.step === CONSTANTS.CHECKOUT_STEPS.GOTO_CHECKOUT_PAGE ||
						stepData?.step === CONSTANTS.CHECKOUT_STEPS.SELECTED_PAYMENT_METHOD
					) {
						const { itemIds, itemsNumber } = getItemsData(cartFields, restData)

						actionName =
							CONSTANTS.CHECKOUT_STEPS.GOTO_CHECKOUT_PAGE === stepData?.step ? ActionName.BEGIN_CHECKOUT : ActionName.ADD_PAYMENT_INFO

						actionData = {
							[ActionDataKey.COUNTRY_CODE]: restData?.countryCode,
							[ActionDataKey.PRODUCT_ITEM_IDS]: itemIds,
							[ActionDataKey.REVENUE]: formatPriceAsFloat(
								cartFields?.grandTotal,
								restData?.currency,
								restData?.countryCode,
								1,
								false,
								true
							),
							[ActionDataKey.CURRENCY]: restData?.currency,
							[ActionDataKey.STORE_ID]: stepData?.storeID,
							[ActionDataKey.CHECKOUT_STEP]: stepData?.step,
							[ActionDataKey.STORE_NAME]: stepData?.storeName,
							[ActionDataKey.SHIPPING_TIER]: stepData?.deliveryType || '',
							[ActionDataKey.NUMBER_OF_ITEMS]: itemsNumber,
						} as BeginCheckoutAction
					}

					break
				}
				case enhancedEcommerceEvents.sendViewCartEvent: {
					const [restData, cartFields] = args
					const { itemIds, itemsNumber } = getItemsData(cartFields, restData)

					actionName = ActionName.VIEW_CART

					actionData = {
						[ActionDataKey.CURRENCY]: restData?.currency,
						[ActionDataKey.PRODUCT_ITEM_IDS]: itemIds,
						[ActionDataKey.NUMBER_OF_ITEMS]: itemsNumber,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(
							cartFields?.grandTotal,
							restData?.currency,
							restData?.countryCode,
							1,
							false,
							true
						),
					} as ViewCartAction
					break
				}

				case enhancedEcommerceEvents.sendViewProdutDetailEvent: {
					const [itemData, eventMetaData] = args

					actionName = ActionName.SELECT_ITEM
					actionData = {
						[ActionDataKey.ITEM_ID]: getItemId(itemData),
						[ActionDataKey.CURRENCY]: eventMetaData?.currency,
						[ActionDataKey.ITEM_SECTION_TITLE]: eventMetaData?.sectionTitle,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(
							itemData?.price,
							eventMetaData?.currency,
							eventMetaData?.countryCode,
							1,
							false,
							true
						),
					} as ViewItemAction
					break
				}
				case enhancedEcommerceEvents.sendProductImpressionEvent:
				case enhancedEcommerceEvents.addMenuItemImpression: {
					const [metaData] = args
					const items = metaData?.items || []

					actionName = ActionName.VIEW_ITEM_LIST
					actionData = {
						[ActionDataKey.ITEM_LIST_NAME]: metaData?.listType,
						[ActionDataKey.CURRENCY]: metaData?.currency,
						[ActionDataKey.PRODUCT_ITEM_IDS]: items.map((item: any) => getItemId(item)),
					} as ViewItemListAction
					break
				}
				case enhancedEcommerceEvents.sendRemoveFromCartEvent: {
					const [itemData, eventMetaData] = args

					actionName = ActionName.REMOVE_FROM_CART
					actionData = {
						[ActionDataKey.ITEM_ID]: getItemId(itemData),
						[ActionDataKey.CURRENCY]: eventMetaData?.currency,
						[ActionDataKey.ITEM_SECTION_TITLE]: itemData?.sectionTitle,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(
							itemData?.price,
							eventMetaData?.currency,
							eventMetaData?.countryCode,
							1,
							false,
							true
						),
					} as RemoveFromCartAction
					break
				}
				case enhancedEcommerceEvents.sendSelectPromotionEvent: {
					const [dealData, metaData] = args

					actionName = ActionName.SELECT_PROMOTION
					actionData = {
						[ActionDataKey.ITEM_ID]: dealData?.refId,
						[ActionDataKey.ITEM_NAME]: dealData?.refName,
						[ActionDataKey.TARGET]: dealData?.target,
						[ActionDataKey.CURRENCY]: metaData?.currency,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(dealData?.price, metaData?.currency, metaData?.countryCode, 1, false, true),
					} as SelectPromotionAction
					break
				}
				case enhancedEcommerceEvents.sendViewPromotionEvent: {
					const [dealData, metaData] = args

					actionName = ActionName.VIEW_PROMOTION
					actionData = {
						[ActionDataKey.ITEM_ID]: dealData?.id,
						[ActionDataKey.ITEM_NAME]: dealData?.title,
						[ActionDataKey.CURRENCY]: metaData?.currency,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(dealData?.price, metaData?.currency, metaData?.countryCode, 1, false, true),
					} as ViewPromotionAction
					break
				}
				default: {
					return
				}
			}
		} else if (eventType === 'pageView') {
			// normalize pageView events
		} else {
			const [customEventData] = args

			// old coupon 
			if (customEventData.category === 'discount' && customEventData.action === 'redeem success') {
				actionName = ActionName.DISCOUNT_REDEEM_SUCCESS
				actionData = {
					[ActionDataKey.COUPON_CODE]: customEventData?.label,
					[ActionDataKey.COUPON_NAME]: customEventData?.label,
				} as CouponAction
			}

			switch(customEventData.category) {

				// coupon flow

				case 'remove_coupon': {
					actionName = ActionName.COUPON_REMOVE_COUPON
					actionData = {
						[ActionDataKey.COUPON_CODE]: customEventData?.coupon_code,
						[ActionDataKey.COUPON_NAME]: customEventData?.coupon_name,
					} as CouponAction
					break
				}

				case 'view_coupon_details': {
					actionName = ActionName.COUPON_VIEW_DETAILS
					actionData = {
						[ActionDataKey.COUPON_CODE]: customEventData?.coupon_code,
						[ActionDataKey.COUPON_NAME]: customEventData?.coupon_name,
					} as CouponAction
					break
				}

				case 'apply_coupon': {
					actionName = ActionName.COUPON_APPLY
					actionData = {
						[ActionDataKey.COUPON_CODE]: customEventData?.coupon_code,
						[ActionDataKey.COUPON_NAME]: customEventData?.coupon_name,
					} as CouponAction
					break
				}

				case 'apply_from_popup': {
					actionName = ActionName.COUPON_APPLY_FROM_POPUP
					actionData = {
						[ActionDataKey.COUPON_CODE]: customEventData?.coupon_code,
						[ActionDataKey.COUPON_NAME]: customEventData?.coupon_name,
					} as CouponAction
					break
				}

				case 'filter_coupons': {
					actionName = ActionName.COUPON_FILTER_COUPONS
					actionData = {
						[ActionDataKey.COUPON_FILTER_TYPE]: customEventData?.filter_type,
					} as CouponFilterCouponsAction
					break
				}

				case 'add_coupon': {
					actionName = ActionName.COUPON_ADD_COUPON
					actionData = {
						[ActionDataKey.COUPON_CODE]: customEventData?.coupon_code,
						[ActionDataKey.COUPON_NAME]: customEventData?.coupon_name,
					} as CouponAction
					break
				}


				// New localization flow mapping

				case 'localisationStart': {
					actionName = ActionName.LOCALIZATION_START
					actionData = {
						[ActionDataKey.PAGE_LOCATION]: customEventData?.page_location,
					} as LocalizationStartAction
					break
				}
				
				case 'localisationFinish': {
					actionName = ActionName.LOCALIZATION_FINISH
					actionData = {
						[ActionDataKey.PAGE_LOCATION]: customEventData?.page_location,
						[ActionDataKey.SHIPPING_TIER]: customEventData?.disposition,
						[ActionDataKey.STORE_ID]: customEventData?.storeID,
					} as LocalizationFinishAction
					break
				}

				case LOCALIZATION_EVENTS.SELECT_ORDER_TYPE: {
					actionName = ActionName.LOCALIZATION_SELECT_ORDER_TYPE
					actionData = {
						[ActionDataKey.SHIPPING_TIER]: customEventData?.disposition,
						[ActionDataKey.PAGE_LOCATION]: customEventData?.page_path,
					} as LocalizationSelectOrderTypeAction
					break
				}

				case LOCALIZATION_EVENTS.START_ORDER: {
					actionName = ActionName.LOCALIZATION_START_ORDER
					break
				}

				case LOCALIZATION_EVENTS.TYPED_ADDRESS_MANUALLY: {
					actionName = ActionName.LOCALIZATION_TYPED_ADDRESS_MANUALLY
					break
				}

				case LOCALIZATION_EVENTS.CLICKED_LOCATION_ICON: {
					actionName = ActionName.LOCALIZATION_CLICKED_LOCATION_ICON
					break
				}

				case LOCALIZATION_EVENTS.SAVED_ADDRESSES: {
					actionName = ActionName.LOCALIZATION_SAVED_ADDRESSES
					break
				}

				case LOCALIZATION_EVENTS.SHARED_LOCATION_ON_MAP: {
					actionName = ActionName.LOCALIZATION_SHARED_LOCATION_ON_MAP
					break
				}

				case LOCALIZATION_EVENTS.EDIT_ADDRESS: {
					actionName = ActionName.LOCALIZATION_EDIT_ADDRESS
					break
				}

				case LOCALIZATION_EVENTS.CHANGE_ORDER_TYPE: {
					actionName = ActionName.LOCALIZATION_CHANGE_ORDER_TYPE
					break
				}

				case LOCALIZATION_EVENTS.CHANGE_DELIVERY_ADDRESS: {
					actionName = ActionName.LOCALIZATION_CHANGE_DELIVERY_ADDRESS
					break
				}

				case LOCALIZATION_EVENTS.CHANGE_STORE: {
					actionName = ActionName.LOCALIZATION_CHANGE_STORE
					break
				}

				case LOCALIZATION_EVENTS.ENTER_ADDRESS_MANUALLY: {
					actionName = ActionName.LOCALIZATION_ENTER_ADDRESS_MANUALLY
					break
				}

				case LOCALIZATION_EVENTS.ADD_NEW_ADDRESS: {
					actionName = ActionName.LOCALIZATION_ADD_NEW_ADDRESS
					break
				}

				case LOCALIZATION_EVENTS.DELETE_ADDRESS: {
					actionName = ActionName.LOCALIZATION_DELETE_ADDRESS
					break
				}

				case LOCALIZATION_EVENTS.LOCATION_TYPE: {
					actionName = ActionName.LOCALIZATION_LOCATION_TYPE
					break
				}

				case LOCALIZATION_EVENTS.VIEW_HOME_PAGE_MENU: {
					actionName = ActionName.LOCALIZATION_VIEW_HOME_PAGE_MENU
					break
				}

				// Old localization flow mapping
				
				case 'address box': {
					if (customEventData?.action === 'clicked' && (customEventData?.label === 'map icon' || customEventData?.label === 'map link')) {
						actionName = ActionName.LOCALIZATION_START
						actionData = {
							[ActionDataKey.PAGE_LOCATION]: customEventData?.page_location,
						} as LocalizationStartAction
					} else if (customEventData?.action === 'clicked on pre start my order' || customEventData?.action === 'clicked on start my order') {
						actionName = ActionName.LOCALIZATION_FINISH
						actionData = {
							[ActionDataKey.PAGE_LOCATION]: customEventData?.page_location,
							[ActionDataKey.SHIPPING_TIER]: customEventData?.orderType,
							[ActionDataKey.STORE_ID]: customEventData?.label,
							[ActionDataKey.PRE_ORDER]: customEventData?.action === 'clicked on pre start my order' ? 'true' : 'false',
						} as LocalizationFinishAction
					}
					else if (customEventData?.action === 'button clicked') {
						actionName = ActionName.LOCALIZATION_SELECT_ORDER_TYPE
						actionData = {
							[ActionDataKey.SHIPPING_TIER]: customEventData?.label,
						} as LocalizationSelectOrderTypeAction
					}
					else if (customEventData?.action === 'typed') {
						actionName = ActionName.LOCALIZATION_ENTER_ADDRESS
					}
					else if (customEventData?.action === 'click' && customEventData?.label === 'last address') {
						actionName = ActionName.LOCALIZATION_LAST_ADDRESS
					}
					else if (customEventData?.action === 'click' && customEventData?.label === 'use my location') {
						actionName = ActionName.LOCALIZATION_USE_MY_LOCATION
					}
					break
				}
			}
		}

		if (actionName) {
			return this.analyticsManager.sendEvent(actionName, actionData)
		}
	}
}

export { AnalyticsAdapter }
